import { forwardRef, useId, useState, type ClipboardEvent, type KeyboardEvent } from "react";
import clsx from "clsx";

export interface ChipSuggestion {
  key: string;
  label: string;
  /** Right-aligned: a tag's kind, a ticker's list count. */
  detail?: string;
}

interface ChipInputProps {
  value: string;
  onChange: (next: string) => void;
  /** Enter, a comma, a delimited paste, or leaving the field: add what's typed. */
  onCommit?: (text: string) => void;
  onPick?: (suggestion: ChipSuggestion) => void;
  suggestions?: ChipSuggestion[];
  /**
   * Enter takes the first suggestion. For tag search, where what's typed is
   * only a query; the Tickers box leaves it off, so Enter adds what's typed.
   */
  pickFirstOnEnter?: boolean;
  /** Commit on a comma and on blur, as the Keywords and Tickers boxes do. */
  commitsText?: boolean;
  placeholder: string;
  ariaLabel: string;
  disabled?: boolean;
  /** Below the options, e.g. "No tags match". */
  emptyText?: string;
}

/**
 * The typing field inside a box, after its chips.
 *
 * A combobox when it has suggestions: arrows move through them, Enter or a
 * click takes one, Escape closes them. Suggestions never get in the way of
 * what's typed — in the Tickers box Enter adds the text unless an option was
 * chosen with the arrows.
 */
const ChipInput = forwardRef<HTMLInputElement, ChipInputProps>(function ChipInput(
  {
    value,
    onChange,
    onCommit,
    onPick,
    suggestions = [],
    pickFirstOnEnter = false,
    commitsText = true,
    placeholder,
    ariaLabel,
    disabled,
    emptyText,
  }: ChipInputProps,
  ref
) {
  const listId = useId();
  const [active, setActive] = useState(-1);
  const [open, setOpen] = useState(false);
  const showList = open && value.trim().length > 0 && (suggestions.length > 0 || !!emptyText);
  const highlighted =
    active >= 0 ? active : pickFirstOnEnter && suggestions.length > 0 ? 0 : -1;

  const commit = (text: string) => {
    if (!text.trim()) return;
    onCommit?.(text);
    setActive(-1);
  };

  const pick = (suggestion: ChipSuggestion) => {
    onPick?.(suggestion);
    setActive(-1);
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowDown" && suggestions.length > 0) {
      e.preventDefault();
      setOpen(true);
      setActive((i) => Math.min(suggestions.length - 1, (i < 0 ? highlighted : i) + 1));
      return;
    }
    if (e.key === "ArrowUp" && suggestions.length > 0) {
      e.preventDefault();
      setActive((i) => Math.max(-1, i - 1));
      return;
    }
    if (e.key === "Escape" && showList) {
      e.preventDefault();
      e.stopPropagation();
      setOpen(false);
      setActive(-1);
      return;
    }
    if (e.key === "Enter") {
      e.preventDefault();
      if (showList && highlighted >= 0 && suggestions[highlighted]) {
        pick(suggestions[highlighted]);
      } else if (commitsText) {
        commit(value);
      }
      return;
    }
    if (e.key === "," && commitsText) {
      e.preventDefault();
      commit(value);
    }
  };

  const onPaste = (e: ClipboardEvent<HTMLInputElement>) => {
    if (!commitsText) return;
    const pasted = e.clipboardData.getData("text");
    // A list, not a word: file it now, rather than leave it in the field.
    if (/[\n\r,;\t]/.test(pasted)) {
      e.preventDefault();
      commit(`${value},${pasted}`);
    }
  };

  return (
    <div className="relative min-w-[200px] flex-[1_0_200px]">
      <input
        ref={ref}
        type="text"
        value={value}
        disabled={disabled}
        onChange={(e) => {
          onChange(e.target.value);
          setOpen(true);
          setActive(-1);
        }}
        onKeyDown={onKeyDown}
        onPaste={onPaste}
        onFocus={() => setOpen(true)}
        onBlur={() => {
          setOpen(false);
          setActive(-1);
          if (commitsText) commit(value);
        }}
        placeholder={placeholder}
        aria-label={ariaLabel}
        role={suggestions.length > 0 || emptyText ? "combobox" : undefined}
        aria-expanded={suggestions.length > 0 || emptyText ? showList : undefined}
        aria-controls={showList ? listId : undefined}
        aria-autocomplete={suggestions.length > 0 || emptyText ? "list" : undefined}
        aria-activedescendant={
          showList && highlighted >= 0 ? `${listId}-${highlighted}` : undefined
        }
        autoComplete="off"
        spellCheck={false}
        className="h-7 w-full border-0 bg-transparent px-1 py-0 text-sm text-gray-100 placeholder:text-gray-500 focus:outline-none focus:ring-0 disabled:opacity-50"
      />
      {showList && (
        <ul
          id={listId}
          role="listbox"
          aria-label={ariaLabel}
          className="absolute left-0 top-full z-20 mt-1 w-full min-w-[240px] max-w-[360px] overflow-hidden rounded-md border border-gray-700 bg-gray-900 py-1 shadow-lg shadow-black/40"
        >
          {suggestions.map((suggestion, i) => (
            <li
              key={suggestion.key}
              id={`${listId}-${i}`}
              role="option"
              aria-selected={i === highlighted}
              // Keep focus in the field, so blur doesn't commit the text first.
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => pick(suggestion)}
              onMouseEnter={() => setActive(i)}
              className={clsx(
                "flex h-8 cursor-pointer items-center gap-3 px-2.5 text-[13px]",
                i === highlighted ? "bg-squawk-blue/10 text-gray-100" : "text-gray-200"
              )}
            >
              <span className="min-w-0 flex-1 truncate">{suggestion.label}</span>
              {suggestion.detail && (
                <span className="shrink-0 text-xs text-gray-400">{suggestion.detail}</span>
              )}
            </li>
          ))}
          {suggestions.length === 0 && emptyText && (
            <li className="px-2.5 py-1.5 text-xs text-gray-400">{emptyText}</li>
          )}
        </ul>
      )}
    </div>
  );
});

export default ChipInput;
